import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Product } from "@/mocks/products";

interface Props {
  product: Product;
}

const categoryLabel: Record<string, string> = {
  dzi: "西藏天珠",
  crystal: "天然水晶礦石",
  jade: "翡翠玉石",
  thangka: "唐卡佛牌",
};

const categoryColor: Record<string, string> = {
  dzi: "#8b6f47",
  crystal: "#7a6a9a",
  jade: "#4a7a52",
  thangka: "#c9a96e",
};

export default function ProductDetailHero({ product }: Props) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSpec, setSelectedSpec] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  const specs = [
    { label: "標準款", desc: "單顆珠徑 8mm" },
    { label: "精緻款", desc: "單顆珠徑 10mm" },
    { label: "典藏款", desc: "單顆珠徑 12mm" },
  ];

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [product.id]);

  const formatPrice = (price: number) => `NT$ ${price.toLocaleString("zh-TW")}`;
  const color = categoryColor[product.category];

  const handleAddCart = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  return (
    <section className="pt-20 bg-[#faf8f4]">
      <div className="max-w-7xl mx-auto px-6 md:px-16 py-12 md:py-20">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-10 text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <button onClick={() => navigate("/")} className="text-[#a89880] hover:text-[#8b6f47] transition-colors cursor-pointer whitespace-nowrap">首頁</button>
          <i className="ri-arrow-right-s-line text-[#c9b89a]"></i>
          <button onClick={() => navigate("/products")} className="text-[#a89880] hover:text-[#8b6f47] transition-colors cursor-pointer whitespace-nowrap">商品系列</button>
          <i className="ri-arrow-right-s-line text-[#c9b89a]"></i>
          <button onClick={() => navigate(`/products?category=${product.category}`)} className="text-[#a89880] hover:text-[#8b6f47] transition-colors cursor-pointer whitespace-nowrap">
            {categoryLabel[product.category]}
          </button>
          <i className="ri-arrow-right-s-line text-[#c9b89a]"></i>
          <span className="text-[#2d2520] font-medium truncate max-w-[200px]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Image */}
          <div
            className={`transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <div className="relative rounded-3xl overflow-hidden bg-white aspect-square">
              <img
                src={product.img}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
              {/* Badges */}
              <div className="absolute top-5 left-5 flex flex-col gap-2">
                {product.badge && (
                  <span
                    className="px-4 py-1.5 text-white text-[0.9375rem] rounded-full font-medium"
                    style={{ backgroundColor: color, fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {product.badge}
                  </span>
                )}
                {product.tag && (
                  <span
                    className="px-4 py-1.5 bg-[#1e1a16]/75 backdrop-blur-sm text-white text-[0.9375rem] rounded-full font-medium"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {product.tag}
                  </span>
                )}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: "ri-shield-check-line", label: "正品保證" },
                { icon: "ri-gift-line", label: "精美包裝" },
                { icon: "ri-truck-line", label: "全台配送" },
              ].map((b) => (
                <div key={b.label} className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-[#e8e0d5]">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${b.icon} text-[#8b6f47] text-xl`}></i>
                  </div>
                  <span className="text-[#5a4e45] text-[0.875rem] font-medium" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                    {b.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Info */}
          <div
            className={`transition-all duration-1000 delay-150 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            {/* Category */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className="text-[0.9375rem] font-medium tracking-wide"
                style={{ color, fontFamily: "'Noto Serif TC', serif" }}
              >
                {categoryLabel[product.category]}
              </span>
              <span className="text-[#c9b89a]">·</span>
              <span className="text-[#a89880] text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                {product.subcategory}
              </span>
            </div>

            <h1
              className="text-[#2d2520] font-bold mb-4 leading-[1.3]"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontFamily: "'Noto Serif TC', serif" }}
            >
              {product.name}
            </h1>

            {/* Stars */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#c9a96e] text-lg"></i>
                ))}
              </div>
              <span className="text-[#7a6a5a] text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                5.0 (128 則評價)
              </span>
            </div>

            <p
              className="text-[#7a6a5a] leading-[1.85] mb-8 text-[1.0625rem]"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              {product.detail}
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-8">
              <span
                className="text-[#2d2520] font-bold"
                style={{ fontSize: "2rem", fontFamily: "'Noto Serif TC', serif" }}
              >
                {formatPrice(product.price)}
              </span>
              <span className="text-[#a89880] text-[1rem] line-through" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                {formatPrice(Math.round(product.price * 1.2))}
              </span>
              <span
                className="px-2.5 py-1 text-white text-[0.875rem] rounded-full font-medium"
                style={{ backgroundColor: color, fontFamily: "'Noto Serif TC', serif" }}
              >
                8折
              </span>
            </div>

            {/* Spec Selector */}
            <div className="mb-8">
              <p className="text-[#5a4e45] font-medium mb-3 text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                規格選擇
              </p>
              <div className="flex flex-wrap gap-3">
                {specs.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedSpec(i)}
                    className={`px-5 py-2.5 rounded-xl border text-[0.9375rem] cursor-pointer transition-all duration-200 whitespace-nowrap ${
                      selectedSpec === i
                        ? "border-[#8b6f47] bg-[#f5ede0] text-[#8b6f47] font-semibold"
                        : "border-[#e8e0d5] text-[#7a6a5a] hover:border-[#8b6f47]"
                    }`}
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    <span className="font-medium">{s.label}</span>
                    <span className="text-[0.8125rem] ml-1.5 opacity-70">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <p className="text-[#5a4e45] font-medium mb-3 text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                數量
              </p>
              <div className="flex items-center gap-0 border border-[#e8e0d5] rounded-xl overflow-hidden w-fit">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-12 h-12 flex items-center justify-center text-[#5a4e45] hover:bg-[#f5ede0] hover:text-[#8b6f47] transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-lg"></i>
                </button>
                <span
                  className="w-14 text-center text-[#2d2520] font-semibold text-[1.0625rem]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                  className="w-12 h-12 flex items-center justify-center text-[#5a4e45] hover:bg-[#f5ede0] hover:text-[#8b6f47] transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={handleAddCart}
                className={`flex-1 py-4 rounded-xl text-[1rem] font-semibold tracking-wide cursor-pointer transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2 ${
                  addedToCart
                    ? "bg-[#4a7a52] text-white"
                    : "bg-[#2d2520] text-white hover:bg-[#3d3530]"
                }`}
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {addedToCart ? (
                  <><i className="ri-check-line text-xl"></i>已加入購物車</>
                ) : (
                  <><i className="ri-shopping-bag-line text-xl"></i>加入購物車</>
                )}
              </button>
              <button
                className="flex-1 py-4 rounded-xl text-[1rem] font-semibold tracking-wide cursor-pointer transition-all duration-300 whitespace-nowrap border-2 hover:bg-[#f5ede0]"
                style={{ borderColor: color, color, fontFamily: "'Noto Serif TC', serif" }}
              >
                立即購買
              </button>
            </div>

            {/* Share */}
            <div className="flex items-center gap-4 pt-6 border-t border-[#e8e0d5]">
              <span className="text-[#a89880] text-[0.9375rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>分享：</span>
              {[
                { icon: "ri-line-line", color: "#06C755" },
                { icon: "ri-facebook-circle-line", color: "#1877f2" },
                { icon: "ri-instagram-line", color: "#e1306c" },
              ].map((s) => (
                <button
                  key={s.icon}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-[#e8e0d5] hover:border-[#8b6f47] transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
